import React, { useMemo } from 'react';
import type { TableColumn } from '@app-types';
import { ReportWidget, ChartType } from '@app-types/visualization';
import { RZTable } from '@components/common/table';
import ChartMetricCard from './ChartMetricCard';
import BarChart from './BarChart';
import StackedBarChart from './StackedBarChart';
import DualAxisBarChart from './DualAxisBarChart';
import LineChart from './LineChart';
import PieChart from './PieChart';
import sharedStyles from './charts.module.css';
import styles from './ChartRenderer.module.css';

interface ChartRendererProps {
  widget: ReportWidget;
  data: Record<string, unknown>[];
}

const DEFAULT_COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316'];

const ChartRenderer: React.FC<ChartRendererProps> = ({ widget, data }) => {
  // Use new protocol if available, otherwise fall back to legacy
  const chartType = widget.chartType || widget.visualization_type;
  const config = widget.config;
  const colors = config?.colors || DEFAULT_COLORS;

  // Generate dynamic columns from first data row (hooks must be at top level)
  const columns: TableColumn[] = useMemo(() => {
    if (!data || data.length === 0) return [];
    return Object.keys(data[0]).map((key) => ({
      key,
      label: key.replace(/(_|\b\w)/g, (m, p1) => (p1 === '_' ? ' ' : p1.toUpperCase())),
      sortable: false,
      defaultVisible: true
    }));
  }, [data]);

  if (!data || data.length === 0) {
    return <div>No data available</div>;
  }

  // Single value metric display
  if (chartType === 'metric' || (data.length === 1 && Object.keys(data[0]).length === 1)) {
    const key = Object.keys(data[0])[0];
    const value = Object.values(data[0])[0];
    const title =
      config?.title ||
      widget.visualization_config?.title ||
      key.replace(/(_|\b\w)/g, (m, p1) => (p1 === '_' ? ' ' : p1.toUpperCase()));
    const description = config?.description || widget.visualization_config?.description;

    return <ChartMetricCard value={value} title={title} description={description} color={colors[0]} />;
  }

  // Table view
  if (chartType === 'table') {
    const renderCell = (column: TableColumn, row: Record<string, unknown>) => {
      return <span>{String(row[column.key] ?? '-')}</span>;
    };

    const limitedData = data.slice(0, 10);

    return (
      <div className={styles.widgetTable}>
        <RZTable
          data={limitedData}
          columns={columns}
          getRowKey={(row) => {
            // Try to find a unique identifier, fallback to stringified row
            return String(row.id || row.name || JSON.stringify(row));
          }}
          renderCell={renderCell}
        />
        {data.length > 10 && (
          <div className={sharedStyles.tableInfoFooter}>Showing first 10 of {data.length} results</div>
        )}
      </div>
    );
  }

  // Chart selection based on type
  switch (chartType as ChartType) {
    case 'bar':
      return <BarChart data={data} config={config} colors={colors} />;

    case 'stacked_bar':
      return <StackedBarChart data={data} config={config} colors={colors} />;

    case 'dual_axis_bar':
      return <DualAxisBarChart data={data} config={config} colors={colors} />;

    case 'line':
      return <LineChart data={data} config={config} colors={colors} />;

    case 'pie':
      return <PieChart data={data} config={config} colors={colors} />;

    // Unsupported chart types - fall back to table view
    case 'dual_axis_line':
    default: {
      console.log(`⚠️ Chart type "${chartType}" not yet supported, falling back to table view`);

      const renderFallbackCell = (column: TableColumn, row: Record<string, unknown>) => {
        return <span>{String(row[column.key] ?? '-')}</span>;
      };

      const limitedData = data.slice(0, 10);

      return (
        <div>
          <div className={sharedStyles.chartWarningBanner}>
            Chart type "{chartType}" coming soon - showing data as table
          </div>
          <div className={styles.widgetTable}>
            <RZTable
              data={limitedData}
              columns={columns}
              getRowKey={(row) => {
                // Try to find a unique identifier, fallback to stringified row
                return String(row.id || row.name || JSON.stringify(row));
              }}
              renderCell={renderFallbackCell}
            />
            {data.length > 10 && (
              <div className={sharedStyles.tableInfoFooter}>Showing first 10 of {data.length} results</div>
            )}
          </div>
        </div>
      );
    }
  }
};

export default ChartRenderer;
